// src/App.js
import React, { Suspense, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useRoutes } from 'react-router-dom';

import { CSpinner, useColorModes } from '@coreui/react';
import './scss/style.scss';
import { NotificationContainer } from 'react-notifications';
import 'react-notifications/lib/notifications.css';
import AuthContext from './components/context/JWTAuthContext';

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'));

// Pages
const Login = React.lazy(() => import('./Templates/pages/login/Login'));
const LoginUser = React.lazy(() => import('./Templates/pages/loginUser/Login'));
const ForgotPassword = React.lazy(() => import('./Templates/pages/forgotPassword/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./Templates/pages/resetPassword/ResetPassword'));
const Register = React.lazy(() => import('./Templates/pages/register/Register'));
const Page404 = React.lazy(() => import('./Templates/pages/page404/Page404'));
const Page500 = React.lazy(() => import('./Templates/pages/page500/Page500'));

// Routes publiques (toujours accessibles)
const publicRoutes = [
  { path: '/login', element: <Login /> },
  { path: '/login/user', element: <LoginUser /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/register', element: <Register /> },
  { path: '/404', element: <Page404 /> },
  { path: '/500', element: <Page500 /> },
];

const App = () => {
  const { isColorModeSet, setColorMode } = useColorModes('coreui-free-react-admin-template-theme');
  const storedTheme = useSelector((state) => state.theme);
  const authContext = useContext(AuthContext);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.href.split('?')[1]);
    const theme = urlParams.get('theme') && urlParams.get('theme').match(/^[A-Za-z0-9\s]+/)[0];
  
    if (theme) {
      setColorMode(theme);
    }

    if (isColorModeSet()) {
      return;
    }

    setColorMode(storedTheme);
  }, [setColorMode, isColorModeSet, storedTheme]);

  // Construire les routes en fonction de l'état d'authentification
  let routes = [...publicRoutes];

  // Si pas encore initialisé, montrer un loader sur toutes les routes
  if (!authContext.isInitialised) {
    routes = [
      ...publicRoutes,
      { 
        path: '/', 
        element: <Loader /> 
      },
      { 
        path: '/test/admin', 
        element: <Loader /> 
      },
      { 
        path: '*', 
        element: <Loader /> 
      },
    ];
  } 
  // Si non authentifié, rediriger vers login
  else if (!authContext.isAuthenticated) {
    routes = [
      ...publicRoutes,
      { 
        path: '/', 
        element: <Login /> 
      },
      { 
        path: '/test/admin', 
        element: <Login /> 
      },
      { 
        path: '*', 
        element: <Login /> 
      },
    ];
  } 
  // Si authentifié, montrer le layout protégé
  else {
    routes = [
      ...publicRoutes,
      { 
        path: '/', 
        element: <DefaultLayout /> 
      },
      { 
        path: '/test/admin', 
        element: <DefaultLayout /> 
      },
      { 
        path: '/*', 
        element: <DefaultLayout /> 
      },
    ];
  }

  const content = useRoutes(routes);

  return (
    <>
      <Suspense fallback={<Loader />}>
        {content}
        <NotificationContainer />
      </Suspense>
    </>
  );
};

// Composant Loader réutilisable
const Loader = () => (
  <div className="pt-3 text-center">
    <CSpinner color="primary" variant="grow" />
  </div>
);

export default App;